import { z } from 'zod';

import { NO_POS, type SourcePos } from '../ast.ts';
import type { EvalResult } from '../env.ts';
import { ShapeError } from '../errors.ts';
import { type AstInput, astShape, opCallPos } from '../normalize.ts';
import { opMeta, type RowFn, type SpecialCtx } from '../op-meta.ts';
import { elemPath, paramPath } from '../path.ts';
import { astArg } from '../schema/ast-codec.ts';
import { unifyColumnTypes } from '../schema/infer.ts';
import { type ColumnType, nullableCol, unknownCol, unwrapNullable } from '../schema/types.ts';
import type { Value } from '../value.ts';
import { defineBinary, isNullish } from './helpers.ts';

// Conditionals. Lisp truthiness — only `false`, `null`, `undefined`
// are falsy; `0`, `""`, `NaN` are truthy. Deliberately distinct from
// the JS-`!`-based truthiness of `not`/`and`/`or` so numeric/string
// columns don't silently fall through a branch. SSOT for the
// conditional-truthiness rule, shared by `if` and `case`.
const isTruthy = (v: unknown): boolean => v !== false && !isNullish(v);

// `if`/`case` are special forms so the non-taken branch is NOT
// evaluated at top level (true short-circuit via `ctx.evalNode`).
// Each also carries a `compileExpr` so it composes inside
// derive/filter expressions (per-row; both branches are compiled but
// only the taken one runs per row) and an `inferType` for
// schema propagation.
export const ifOp = z
  .object({ name: z.literal('if'), params: z.array(astArg()).min(2).max(3) })
  .describe(
    'Conditional. (if cond then [else]) — lisp truthiness (only false/null/undefined are falsy; 0, "", NaN are truthy). Missing else ⇒ null. The non-taken branch is not evaluated.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(if (> 3 1) "y" "n")', result: '"y"' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const cond = (await ctx.evalNode(data.params[0] as AstInput, ctx.env, paramPath(ctx.path, 0))).value;
      if (isTruthy(cond)) {
        return {
          value: (await ctx.evalNode(data.params[1] as AstInput, ctx.env, paramPath(ctx.path, 1))).value,
          env: ctx.env,
        };
      }
      const elseNode = data.params[2];
      if (elseNode === undefined) {
        return { value: null, env: ctx.env };
      }
      return {
        value: (await ctx.evalNode(elseNode as AstInput, ctx.env, paramPath(ctx.path, 2))).value,
        env: ctx.env,
      };
    },
    expr: {
      compileExpr: async (call, compile, path): Promise<RowFn> => {
        const params = call.params ?? [];
        const [cond, thenFn, elseFn] = await Promise.all([
          compile(params[0], paramPath(path, 0)),
          compile(params[1], paramPath(path, 1)),
          params[2] === undefined ? Promise.resolve(undefined) : compile(params[2], paramPath(path, 2)),
        ]);
        return (row) => {
          if (isTruthy(cond(row))) {
            return thenFn(row);
          }
          return elseFn === undefined ? null : elseFn(row);
        };
      },
      inferType: (call, infer, path): ColumnType => {
        const params = call.params ?? [];
        const thenT = unwrapNullable(infer(params[1], paramPath(path, 1)));
        const elseRaw = params[2];
        const elseT = elseRaw === undefined ? unknownCol : unwrapNullable(infer(elseRaw, paramPath(path, 2)));
        const unified = unifyColumnTypes([thenT, elseT]);
        return elseRaw === undefined ? nullableCol(unified) : unified;
      },
    },
  });

// A `case` clause is a 2-element vector `[cond val]`; a trailing
// 1-element vector `[default]` is the fallback. Vector clauses (not
// paren `(else …)`) because the s-expr parser reserves a paren list's
// head for an op name — same semantics, parser-safe.
// `parseCaseClause` is the SSOT for the
// clause shape rule; `apply`, `compileExpr`, and `inferType` all
// consume the normalized discriminated form (no per-method re-parse).
type CaseClause =
  | { readonly kind: 'default'; readonly val: AstInput }
  | { readonly kind: 'clause'; readonly cond: AstInput; readonly val: AstInput };

const parseCaseClause = (node: AstInput, index: number, pos: SourcePos): CaseClause => {
  const shape = astShape(node);
  if (shape.kind !== 'vector' || shape.elements.length < 1 || shape.elements.length > 2) {
    throw new ShapeError(
      `case clause #${index} has ${shape.kind === 'vector' ? shape.elements.length : 0} element(s); ` +
        'each clause must be a 2-element [cond val] vector, with an optional trailing 1-element [default] vector.',
      pos,
      `#${index}`,
    );
  }
  const [a, b] = shape.elements;
  return b === undefined
    ? { kind: 'default', val: a as AstInput }
    : { kind: 'clause', cond: a as AstInput, val: b as AstInput };
};

export const caseOp = z
  .object({ name: z.literal('case'), params: z.array(astArg()).min(1) })
  .describe(
    'Multi-branch conditional. (case [cond val] [cond val] … [default]) — clauses are 2-element vectors evaluated top-down; the first truthy cond wins. A trailing 1-element vector is the default. No match and no default ⇒ null. Lisp truthiness; non-taken branches are not evaluated.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(case [(> 5 10) "big"] [(> 5 1) "mid"] ["small"])', result: '"mid"' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      for (let i = 0; i < data.params.length; i++) {
        const clause = parseCaseClause(data.params[i] as AstInput, i, callPos);
        const clausePath = paramPath(ctx.path, i);
        if (clause.kind === 'default') {
          return { value: (await ctx.evalNode(clause.val, ctx.env, elemPath(clausePath, 0))).value, env: ctx.env };
        }
        const cond = (await ctx.evalNode(clause.cond, ctx.env, elemPath(clausePath, 0))).value;
        if (isTruthy(cond)) {
          return { value: (await ctx.evalNode(clause.val, ctx.env, elemPath(clausePath, 1))).value, env: ctx.env };
        }
      }
      return { value: null, env: ctx.env };
    },
    expr: {
      compileExpr: async (call, compile, path): Promise<RowFn> => {
        const params = call.params ?? [];
        const clauses = await Promise.all(
          params.map(async (p, i) => {
            const clause = parseCaseClause(p as AstInput, i, NO_POS);
            const cp = paramPath(path, i);
            if (clause.kind === 'default') {
              return { kind: 'default', val: await compile(clause.val, elemPath(cp, 0)) } as const;
            }
            const [cond, val] = await Promise.all([
              compile(clause.cond, elemPath(cp, 0)),
              compile(clause.val, elemPath(cp, 1)),
            ]);
            return { kind: 'clause', cond, val } as const;
          }),
        );
        return (row) => {
          for (const c of clauses) {
            if (c.kind === 'default') {
              return c.val(row);
            }
            if (isTruthy(c.cond(row))) {
              return c.val(row);
            }
          }
          return null;
        };
      },
      inferType: (call, infer, path): ColumnType => {
        const params = call.params ?? [];
        const valTypes = params.map((p, i) => {
          const clause = parseCaseClause(p as AstInput, i, NO_POS);
          const cp = paramPath(path, i);
          return clause.kind === 'default'
            ? unwrapNullable(infer(clause.val, elemPath(cp, 0)))
            : unwrapNullable(infer(clause.val, elemPath(cp, 1)));
        });
        const unified = unifyColumnTypes(valTypes.length > 0 ? valTypes : [unknownCol]);
        const hasDefault = params.some((p, i) => parseCaseClause(p as AstInput, i, NO_POS).kind === 'default');
        return hasDefault ? unified : nullableCol(unified);
      },
    },
  });

const nullIfBase = defineBinary((a, b) => (a === b ? null : (a as Value)));
export const nullIf = z
  .object({ name: z.literal('null-if'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(null-if x sentinel) — null when x is strictly equal (===) to sentinel, otherwise x.')
  .register(opMeta, {
    ...nullIfBase,
    example: { expr: '(null-if 5 5)', result: 'null' },
    expr: {
      ...nullIfBase.expr,
      inferType: (call, infer, path): ColumnType =>
        nullableCol(unwrapNullable(infer((call.params ?? [])[0], paramPath(path, 0)))),
    },
  });
